import {
  Banner,
  Button,
  Container,
  Header,
  Layout,
  Level,
  LevelGroup,
  LevelItem,
  MainContent,
  ScaffoldCard,
  ScaffoldCards,
  Section,
  Text,
} from '@inmotionnow/momentum-components-react'
import { StatusCodes } from 'http-status-codes'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'

import {
  type BulkImportRunSummary,
  useGetBulkImportRunsQuery,
} from '@/features/bulk-import/bulkImportApi'

/** How many runs one request asks for. The endpoint's own default, well under its cap. */
export const BULK_IMPORT_HISTORY_PAGE_SIZE = 50

const createdAtFormat = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
})

function readStatus(error: unknown): unknown {
  return typeof error === 'object' && error !== null && 'status' in error
    ? (error as { status: unknown }).status
    : undefined
}

/**
 * Succeeded, derived. `created_count` counts people by identity link and overlaps the
 * other counts, so it is not a success count; what is neither failed nor still moving
 * is what worked.
 */
function succeededCount(run: BulkImportRunSummary): number {
  return Math.max(0, run.total_count - run.failed_count - run.in_progress_count)
}

const BulkImportHistoryEntry = ({
  accountId,
  run,
}: {
  accountId: number
  run: BulkImportRunSummary
}) => {
  const { t } = useTranslation()
  return (
    <ScaffoldCard>
      <div data-testid={`bulk-import-history-entry-${run.id}`}>
        <Level>
          <LevelGroup position='left'>
            <LevelItem>
              <Link to={`/account/${accountId}/imports/${run.id}`}>
                {createdAtFormat.format(new Date(run.created_at))}
              </Link>
            </LevelItem>
            <LevelItem>
              <Text size='sm' color='muted'>
                {run.initiated_by_is_staff
                  ? `${t('BULK_IMPORT_HISTORY_BY_STAFF')} (${run.initiated_by})`
                  : run.initiated_by}
              </Text>
            </LevelItem>
          </LevelGroup>
          <LevelGroup position='right'>
            <LevelItem>
              <Text size='sm' textWeight='bold'>
                {t(`BULK_IMPORT_STATUS_${run.status.toUpperCase()}`)}
              </Text>
            </LevelItem>
          </LevelGroup>
        </Level>
        <div data-testid={`bulk-import-history-counts-${run.id}`}>
          <Text size='sm' color='muted'>
            {[
              `${t('BULK_IMPORT_HISTORY_TOTAL')}: ${run.total_count}`,
              `${t('BULK_IMPORT_HISTORY_SUCCEEDED')}: ${succeededCount(run)}`,
              `${t('BULK_IMPORT_HISTORY_FAILED')}: ${run.failed_count}`,
              `${t('BULK_IMPORT_HISTORY_IN_PROGRESS')}: ${run.in_progress_count}`,
            ].join(' · ')}
          </Text>
        </div>
      </div>
    </ScaffoldCard>
  )
}

/**
 * One window of the history. Each loaded window is its own query, so a page that has
 * been loaded stays cached and is refreshed with the rest when the list is invalidated,
 * with nothing to merge by hand.
 *
 * Windows are fetched at different times and the list is newest first, so an import
 * started in between shifts every offset by one and the last run of one window comes
 * back at the top of the next. Runs an earlier window already shows are left out here.
 */
const BulkImportHistoryPage = ({
  accountId,
  index,
  isLast,
  hiddenIds,
  onIdsLoaded,
  onLoadMore,
}: {
  accountId: number
  index: number
  isLast: boolean
  hiddenIds: ReadonlySet<number>
  onIdsLoaded: (index: number, ids: number[]) => void
  onLoadMore: () => void
}) => {
  const { t } = useTranslation()
  const offset = index * BULK_IMPORT_HISTORY_PAGE_SIZE
  // Refetched on every visit: a retry in another tab, a colleague's, or a run finishing
  // on its own cannot invalidate this tab's cache, and this is the screen people come
  // back to.
  const { data, isLoading, isError, error } = useGetBulkImportRunsQuery(
    { accountId, limit: BULK_IMPORT_HISTORY_PAGE_SIZE, offset },
    { refetchOnMountOrArgChange: true },
  )

  React.useEffect(() => {
    if (data)
      onIdsLoaded(
        index,
        data.map(run => run.id),
      )
  }, [data, index, onIdsLoaded])

  if (isLoading) return <Text tag='p'>{t('LOADING')}</Text>
  if (isError || !data) {
    return (
      <Text tag='p' dataTestingId='bulk-import-history-error'>
        {t(
          readStatus(error) === StatusCodes.FORBIDDEN
            ? 'BULK_IMPORT_HISTORY_FORBIDDEN'
            : 'BULK_IMPORT_HISTORY_LOAD_FAILED',
        )}
      </Text>
    )
  }
  if (offset === 0 && data.length === 0) {
    return <Text tag='p'>{t('BULK_IMPORT_HISTORY_EMPTY')}</Text>
  }

  return (
    <>
      <ScaffoldCards>
        {data
          .filter(run => !hiddenIds.has(run.id))
          .map(run => (
            <BulkImportHistoryEntry
              key={run.id}
              accountId={accountId}
              run={run}
            />
          ))}
      </ScaffoldCards>
      {/* A full window may have more behind it — the endpoint gives no total, and a
          short window is the only signal that the history has ended. */}
      {isLast && data.length === BULK_IMPORT_HISTORY_PAGE_SIZE && (
        <Button
          label={t('BULK_IMPORT_HISTORY_LOAD_MORE')}
          onClick={onLoadMore}
        />
      )}
    </>
  )
}

/**
 * An account's past bulk imports, newest first, each opening its run page. The way back
 * to an import after the tab that started it is closed — without it the
 * download-correct-resend recovery has no run to send the corrected file to.
 */
const BulkImportHistoryView = () => {
  const { t } = useTranslation()
  const { accountId } = useParams<{ accountId: string }>()
  const [pageCount, setPageCount] = React.useState(1)
  // The run ids each loaded window returned, so a later window can leave out the runs an
  // earlier one already shows.
  const [idsByPage, setIdsByPage] = React.useState<number[][]>([])

  const handleIdsLoaded = React.useCallback((index: number, ids: number[]) => {
    setIdsByPage(previous => {
      const current = previous[index]
      if (
        current &&
        current.length === ids.length &&
        current.every((id, position) => id === ids[position])
      ) {
        return previous
      }
      const next = [...previous]
      next[index] = ids
      return next
    })
  }, [])

  // For each page, the ids every earlier page already shows — built once per change
  // rather than per page per render.
  const hiddenIdsByPage = React.useMemo(() => {
    const result: ReadonlySet<number>[] = []
    const seen = new Set<number>()
    for (let index = 0; index < pageCount; index += 1) {
      result.push(new Set(seen))
      for (const id of idsByPage[index] ?? []) seen.add(id)
    }
    return result
  }, [idsByPage, pageCount])

  const parsedAccountId = React.useMemo(() => {
    if (!accountId || !/^\d+$/.test(accountId)) return undefined
    const parsed = parseInt(accountId, 10)
    return parsed > 0 ? parsed : undefined
  }, [accountId])

  const chrome = (children: React.ReactNode) => (
    <Layout>
      <Header>
        <Banner title={t('BULK_IMPORT_HISTORY')} />
      </Header>
      <MainContent>
        <Section>
          <Container size='lg'>{children}</Container>
        </Section>
      </MainContent>
    </Layout>
  )

  if (parsedAccountId === undefined) {
    return chrome(<Text tag='p'>{t('BULK_IMPORT_HISTORY_NOT_FOUND')}</Text>)
  }

  return chrome(
    <div data-testid='bulk-import-history'>
      {Array.from({ length: pageCount }, (_, index) => (
        <BulkImportHistoryPage
          key={index}
          accountId={parsedAccountId}
          index={index}
          isLast={index === pageCount - 1}
          hiddenIds={hiddenIdsByPage[index]}
          onIdsLoaded={handleIdsLoaded}
          onLoadMore={() => setPageCount(count => count + 1)}
        />
      ))}
    </div>,
  )
}

export { BulkImportHistoryView }
